import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, isSuperAdmin } from "@/lib/auth";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Vault, Plus, RefreshCw, Search, ShieldAlert, ArrowRight, History, Download, Upload, FileText } from "lucide-react";

// ---------------------------------------------------------------------------
// Vault Dockets — CORRECTIVE IMPLEMENTATION: actual Kandake document
// repository. Upload document → classify/describe → Kandake auto-generates
// system IDs → Save → file stored in Kandake Vault storage → authorised
// users retrieve it from the record. The legacy transfer/status machine,
// permissions, sensitivity classes and audit conventions are preserved.
// ---------------------------------------------------------------------------

const STATUS_STYLE = {
  NOT_REQUIRED:                       { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  REQUESTED:                          { bg: "hsl(40 55% 94%)",  fg: "hsl(35 60% 28%)",  border: "hsl(40 55% 76%)" },
  APPROVED_FOR_TRANSFER:              { bg: "hsl(210 45% 94%)", fg: "hsl(210 55% 30%)", border: "hsl(210 45% 74%)" },
  TRANSFERRED_PENDING_VERIFICATION:   { bg: "hsl(40 55% 94%)",  fg: "hsl(35 60% 28%)",  border: "hsl(40 55% 76%)" },
  VERIFIED_IN_CONTROLLED_STORAGE:     { bg: "hsl(150 45% 92%)", fg: "hsl(150 45% 24%)", border: "hsl(150 40% 66%)" },
  RETURNED:                           { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  REVOKED:                            { bg: "hsl(0 55% 94%)",   fg: "hsl(0 65% 30%)",   border: "hsl(0 55% 72%)" },
  EXCEPTION_REVIEW_REQUIRED:          { bg: "hsl(0 55% 94%)",   fg: "hsl(0 65% 30%)",   border: "hsl(0 55% 72%)" },
};

function StatusBadge({ value }) {
  const st = STATUS_STYLE[value] || STATUS_STYLE.REQUESTED;
  return <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ background: st.bg, color: st.fg, borderColor: st.border }}>{value.replaceAll("_", " ")}</Badge>;
}

function canWrite(user) {
  return !!user && ["super_admin", "gfmp_reviewer"].includes(user.role);
}

function NewDocketDialog({ vocab, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    material_category: "OTHER", sensitivity_level: "STANDARD",
    description: "", external_original_reference: "",
    source_record_type: "", source_record_id: "", custody_notes: "",
  });
  const reset = () => {
    setFile(null);
    setForm({ material_category: "OTHER", sensitivity_level: "STANDARD",
      description: "", external_original_reference: "",
      source_record_type: "", source_record_id: "", custody_notes: "" });
  };
  const submit = async () => {
    if (!file) { toast.error("Select the document file to upload."); return; }
    if (!form.external_original_reference.trim()) {
      toast.error("External / Original Reference Number is required — enter the document's pre-existing reference, or N/A."); return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      const r = await api.post("/vault-dockets/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
      });
      onCreated?.(r.data);
      toast.success(`Document stored in the Vault — ${r.data.vault_reference_id}`);
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild><Button data-testid="vd-new" className="gap-2"><Plus className="w-4 h-4" /> New Docket / Upload Document</Button></DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Upload Document to Vault</DialogTitle>
          <DialogDescription>
            The file is stored inside Kandake's Vault storage. Kandake generates the internal
            Docket ID and Vault reference automatically — you never enter a system ID or a
            storage location.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Document file <span className="text-destructive">*</span></Label>
            <Input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.wav,.mp3,.flac,.aiff,.zip"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="vd-file-input"
            />
            {file && <p className="text-xs text-muted-foreground mt-1" data-testid="vd-file-chosen">{file.name} · {(file.size / (1024 * 1024)).toFixed(2)} MB</p>}
          </div>
          <div>
            <Label>Document type</Label>
            <Select value={form.material_category} onValueChange={(v)=>setForm({...form, material_category: v})}>
              <SelectTrigger data-testid="vd-material"><SelectValue /></SelectTrigger>
              <SelectContent>{(vocab.material_categories || []).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sensitivity</Label>
            <Select value={form.sensitivity_level} onValueChange={(v)=>setForm({...form, sensitivity_level: v})}>
              <SelectTrigger data-testid="vd-sens"><SelectValue /></SelectTrigger>
              <SelectContent>{(vocab.sensitivity_levels || []).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Description / title</Label>
            <Input value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} placeholder="e.g. Executed admin agreement — J. Writer" data-testid="vd-description" maxLength={500} />
          </div>
          <div className="col-span-2">
            <Label>External / Original Reference Number <span className="text-destructive">*</span></Label>
            <Input value={form.external_original_reference} onChange={(e)=>setForm({...form, external_original_reference: e.target.value})} placeholder="Pre-existing reference (reg. no., agreement no., …) — or N/A" data-testid="vd-ext-ref" maxLength={200} />
            <p className="text-[11px] text-muted-foreground mt-1">The document's real-world identifier from before it entered Kandake. Enter <span className="font-mono">N/A</span> if it has none. This is separate from Kandake's own system IDs.</p>
          </div>
          <div><Label>Associated record type (optional)</Label><Input value={form.source_record_type} onChange={(e)=>setForm({...form, source_record_type: e.target.value})} data-testid="vd-src-type" placeholder="track / writer / client / …" /></div>
          <div><Label>Associated record id (optional)</Label><Input value={form.source_record_id} onChange={(e)=>setForm({...form, source_record_id: e.target.value})} data-testid="vd-src-id" /></div>
          <div className="col-span-2"><Label>Notes (optional)</Label><Textarea rows={2} value={form.custody_notes} onChange={(e)=>setForm({...form, custody_notes: e.target.value})} data-testid="vd-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="vd-save">{saving ? "Saving…" : "Save to Vault"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransitionDialog({ docket, vocab, onDone }) {
  const [open, setOpen] = useState(false);
  const [toStatus, setToStatus] = useState("");
  const [reason, setReason] = useState("");
  const [custody, setCustody] = useState("");
  const [verify, setVerify] = useState("");
  const [exception, setException] = useState("");
  const [external, setExternal] = useState(docket.external_storage_reference || "");
  const [saving, setSaving] = useState(false);

  const allowed = ((vocab.allowed_transitions || {})[docket.status] || []);

  const submit = async () => {
    if (!toStatus) { toast.error("Choose a target status."); return; }
    if (!reason.trim()) { toast.error("Reason is required."); return; }
    setSaving(true);
    try {
      const r = await api.post(`/vault-dockets/${docket.id}/status`, {
        to_status: toStatus, reason: reason.trim(),
        custody_notes: custody.trim(),
        verification_notes: verify.trim(),
        exception_notes: exception.trim(),
        external_storage_reference: external.trim(),
      });
      onDone?.(r.data);
      toast.success("Transition recorded. Kandake did NOT move files.");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transition failed.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="gap-1" data-testid={`vd-transition-${docket.id}`} disabled={allowed.length === 0}><ArrowRight className="w-3.5 h-3.5" /> Change status</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change docket status</DialogTitle>
          <DialogDescription>
            You are recording what YOU did in the physical / offline world. Kandake does not move files.
            Approval and verification are Super Admin only.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Target status</Label>
            <Select value={toStatus} onValueChange={setToStatus}>
              <SelectTrigger data-testid="vd-target"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                {allowed.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reason <span className="text-destructive">*</span></Label>
            <Textarea rows={2} value={reason} onChange={(e)=>setReason(e.target.value)} data-testid="vd-transition-reason" />
          </div>
          <div>
            <Label>External storage reference</Label>
            <Input value={external} onChange={(e)=>setExternal(e.target.value)} placeholder="e.g. Vault-Drive-A / Shelf-3" />
          </div>
          <div><Label>Custody notes</Label><Textarea rows={2} value={custody} onChange={(e)=>setCustody(e.target.value)} /></div>
          {toStatus === "VERIFIED_IN_CONTROLLED_STORAGE" && (
            <div><Label>Verification notes</Label><Textarea rows={2} value={verify} onChange={(e)=>setVerify(e.target.value)} data-testid="vd-verify" /></div>
          )}
          {toStatus === "EXCEPTION_REVIEW_REQUIRED" && (
            <div><Label>Exception notes</Label><Textarea rows={2} value={exception} onChange={(e)=>setException(e.target.value)} data-testid="vd-exception" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="vd-transition-save">{saving ? "Saving…" : "Record transition"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VaultDocketDesk() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sens, setSens] = useState("");
  const [vocab, setVocab] = useState({});
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      if (sens) params.sensitivity_level = sens;
      const r = await api.get("/vault-dockets", { params });
      setRows(r.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }, [q, status, sens]);

  useEffect(() => {
    api.get("/vault-dockets/meta/vocab").then((r)=>setVocab(r.data || {})).catch(()=>{});
    load();
  }, [load]);

  const openDocument = async (r) => {
    try {
      const resp = await api.get(`/vault-dockets/${r.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.file_original_filename || `${r.vault_reference_id || r.id}.bin`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not retrieve the document.");
    }
  };

  const attachDocument = async (r, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const resp = await api.post(`/vault-dockets/${r.id}/file`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
      });
      setRows((rs) => rs.map((row) => (row.id === resp.data.id ? resp.data : row)));
      toast.success("Document stored in the Vault.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="vault-docket-desk">
      <div className="flex flex-wrap items-center gap-3">
        <Vault className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Vault Dockets</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Kandake's document repository. Upload a document, classify it, and Kandake stores the
            actual file with auto-generated system IDs. Authorised operators retrieve it from the
            record. Every action writes an audit row.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">Internal-only</Badge>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="vd-refresh"><RefreshCw className="w-4 h-4" /> Refresh</Button>
        {canWrite(user) && <NewDocketDialog vocab={vocab} onCreated={(x)=>setRows((rs)=>[x, ...rs])} />}
      </div>

      <Card style={{ background: "hsl(40 48% 96%)", borderColor: "hsl(40 48% 62%)" }}>
        <CardContent className="py-3 text-sm flex items-center gap-2" style={{ color: "hsl(38 60% 26%)" }}>
          <ShieldAlert className="w-4 h-4" />
          <span>{vocab.notes || "The Vault stores uploaded documents inside Kandake's controlled storage. Restricted material remains protected by the existing access controls."}</span>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Filters</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><Label>Search</Label><Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="vault ref, source id, reason…" data-testid="vd-filter-q" /></div>
            <div>
              <Label>Status</Label>
              <Select value={status || "__any__"} onValueChange={(v)=>setStatus(v === "__any__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any</SelectItem>
                  {(vocab.statuses || []).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sensitivity</Label>
              <Select value={sens || "__any__"} onValueChange={(v)=>setSens(v === "__any__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any</SelectItem>
                  {(vocab.sensitivity_levels || []).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end"><Button className="w-full" onClick={load} data-testid="vd-filter-apply">Apply</Button></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Dockets ({rows.length})</CardTitle><CardDescription>Approval and verification transitions are Super Admin only. Every event is recorded.</CardDescription></CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
            rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="vd-empty">No dockets recorded.</p> :
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} className="border border-border rounded-md" data-testid={`vd-row-${r.id}`}>
                  <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-xs opacity-70">{r.vault_reference_id}</span>
                    <StatusBadge value={r.status} />
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{r.material_category}</Badge>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{r.sensitivity_level}</Badge>
                    {r.description && <span className="text-xs font-medium max-w-[220px] truncate" data-testid={`vd-desc-${r.id}`}>{r.description}</span>}
                    {r.file_original_filename && (
                      <span className="text-xs opacity-70 inline-flex items-center gap-1" data-testid={`vd-filename-${r.id}`}>
                        <FileText className="w-3 h-3" />{r.file_original_filename}
                      </span>
                    )}
                    {r.source_record_id && <span className="text-xs opacity-70">{r.source_record_type}:{r.source_record_id?.slice(0, 8)}</span>}
                    <div className="flex-1" />
                    {r.file_storage_path ? (
                      <Button variant="outline" size="sm" className="gap-1" onClick={()=>openDocument(r)} data-testid={`vd-open-doc-${r.id}`}>
                        <Download className="w-3.5 h-3.5" /> Open Document
                      </Button>
                    ) : (
                      canWrite(user) && (
                        <label className="inline-flex">
                          <input type="file" className="hidden" onChange={(e)=>{ attachDocument(r, e.target.files?.[0]); e.target.value = ""; }} data-testid={`vd-attach-input-${r.id}`} />
                          <Button variant="outline" size="sm" className="gap-1" asChild>
                            <span className="cursor-pointer"><Upload className="w-3.5 h-3.5" /> Attach document</span>
                          </Button>
                        </label>
                      )
                    )}
                    <Button variant="outline" size="sm" className="gap-1" onClick={()=>setExpanded({ ...expanded, [r.id]: !expanded[r.id] })}><History className="w-3.5 h-3.5" /> Events</Button>
                    {canWrite(user) && <TransitionDialog docket={r} vocab={vocab} onDone={(x)=>setRows((rs)=>rs.map((row)=>row.id===x.id ? x : row))} />}
                  </div>
                  {expanded[r.id] && (
                    <div className="border-t border-border px-4 py-3 text-xs space-y-1">
                      {r.description && <div><span className="opacity-60">Description:</span> {r.description}</div>}
                      <div data-testid={`vd-ext-ref-${r.id}`}><span className="opacity-60">External / Original Reference Number:</span> {r.external_original_reference || "—"}</div>
                      {r.file_storage_path ? (
                        <div><span className="opacity-60">Stored document:</span> {r.file_original_filename} · {((r.file_size || 0) / 1024).toFixed(0)} KB · uploaded {r.file_uploaded_at?.slice(0, 16).replace("T", " ")} by {r.file_uploaded_by_email}</div>
                      ) : (
                        <div><span className="opacity-60">Stored document:</span> none — this docket predates in-Vault storage.</div>
                      )}
                      {r.external_storage_reference && <div><span className="opacity-60">Legacy external storage ref:</span> {r.external_storage_reference} <span className="opacity-60">(legacy pointer; not a stored file)</span></div>}
                      <div><span className="opacity-60">Requested by:</span> {r.transfer_requested_by_email || "—"} at {r.transfer_requested_at || "—"}</div>
                      <div><span className="opacity-60">Approved by:</span> {r.transfer_approved_by_email || "—"} at {r.transfer_approved_at || "—"}</div>
                      <div><span className="opacity-60">Transferred by:</span> {r.transfer_performed_by_email || "—"} at {r.transfer_performed_at || "—"}</div>
                      <div><span className="opacity-60">Verified by:</span> {r.verified_by_email || "—"} at {r.verified_at || "—"}</div>
                      {r.returned_at && <div><span className="opacity-60">Returned by:</span> {r.returned_by_email} at {r.returned_at}</div>}
                      <div><span className="opacity-60">Transfer reason:</span> {r.transfer_reason || "—"}</div>
                      {r.custody_notes && <div><span className="opacity-60">Custody notes:</span> {r.custody_notes}</div>}
                      {r.verification_notes && <div><span className="opacity-60">Verification notes:</span> {r.verification_notes}</div>}
                      {r.exception_notes && <div><span className="opacity-60">Exception notes:</span> {r.exception_notes}</div>}
                      <div className="mt-2 font-semibold">Event log ({(r.events || []).length})</div>
                      <ul className="space-y-1">
                        {(r.events || []).map((e) => (
                          <li key={e.id}><span className="font-mono opacity-60">{e.at}</span> · {e.event} · {e.by_email}{e.reason ? ` — ${e.reason}` : ""}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          }
        </CardContent>
      </Card>
    </div>
  );
}
